import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ListingTable, { ListingTableColumn } from 'common/components/listingTable/ListingTable';
import PaginationWithInfo from 'common/components/pagination/PaginationWithInfo';
import { DEFAULT_PAGINATION_ITEMS_PER_PAGE, LOGGER_LOG_TYPE } from 'Config';
import { useToasts } from 'react-toast-notifications';
import { GenericTypeDto } from 'api/genericTypes/models/GenericTypeDto';
import { GenericTypeSearchCriteria } from 'api/genericTypes/models/GenericTypeSearchCriteria';
import Loading from 'common/services/Loading';
import GenericTypesService from 'api/genericTypes/GenericTypesService';
import Logger from 'common/services/Logger';
import { Paged } from 'api/common/types/Page';
import styles from './GenericTypesList.module.scss';
import InputSearch from 'common/components/inputSearch/InputSearch';
import { useDebouncedCallback } from 'use-debounce';
import Dropdown from 'common/components/dropdown/Dropdown';
import DropdownItem from 'common/components/dropdown/DropdownItem';
import { FaEllipsisH } from 'react-icons/fa';
import QuestionYesNo from 'common/components/questionYesNo/QuestionYesNo';
import { useHistory } from 'react-router-dom';
import { UserProfile } from 'api/account/models/UserProfile';
import UsersService from 'api/users/UsersService';
import { useSelector } from 'react-redux';
import { Reducers } from 'store/types';
import ScreenHeaderButton from 'common/components/screenHeader/ScreenHeaderButton';
import AddIcon from 'assets/svg/desktop_add_new.svg';

type Props = {
};

const GenericTypesList: React.FC<Props> = ({ }: Props) => {
    const { t, i18n } = useTranslation();
    const { addToast } = useToasts();

    const history = useHistory();

    const isFirstLoad = useRef(true);
    const filterJsonKey = 'PARAMETERS_GENERIC_TYPES_FILTER';

    const [genericTypesPage, setGenericTypesPage] = useState<Paged<GenericTypeDto>>();

    const [criteria, setCriteria] = useState<GenericTypeSearchCriteria>({
        itemsPerPage: DEFAULT_PAGINATION_ITEMS_PER_PAGE,
        page: 1,
        orderColumn: 'translated_name',
        orderBy: 'asc'
    });

    const [itemToRemove, setItemToRemove] = useState<GenericTypeDto | null>(null);
    const [showRemoveModal, setShowRemoveModal] = useState<boolean>(false);

    const userProfile = useSelector<Reducers, UserProfile | null>(state => state.authentication.profile);
    const hasGenericTypesWritePolicy = UsersService.hasPolicies(userProfile?.policies || [], ['SETTINGUP_GENERIC_TYPES_WRITE']);

    const showRemoveItemDialog = (item: GenericTypeDto) => {
        setItemToRemove(item);
        setShowRemoveModal(true);
    };

    const onTableFilter = (field: string, isFilterAsc: boolean) => {
        setCriteria({ ...criteria, page: 1, orderBy: (isFilterAsc ? 'asc' : 'desc'), orderColumn: field });
    }

    const debounced = useDebouncedCallback((value: string) => {
        if (value === criteria.allIn) return;
        setCriteria({ ...criteria, page: 1, allIn: value });
    }, 500);

    const renderTableActionCell = (
        row: GenericTypeDto,
    ) => {
        return (
            <Dropdown
                options={
                    <>
                        <DropdownItem url={`/settings/genericTypes/details/${row.id ?? ''}`}>
                            {t('common.details')}
                        </DropdownItem>
                        {hasGenericTypesWritePolicy && <DropdownItem url={`/settings/genericTypes/edit/${row.id ?? ''}`}>
                            {t('common.edit')}
                        </DropdownItem>}
                        {hasGenericTypesWritePolicy && <DropdownItem onClick={() => showRemoveItemDialog(row)} disabled={row.isSystem} disabled_message={t('generic_types.forbidden_delete_tooltip')}>
                            {t('common.remove')}
                        </DropdownItem>}
                    </>
                }
            >
                <div>
                    <FaEllipsisH />
                </div>
            </Dropdown>
        );
    };

    
    const renderNameCell = (row: GenericTypeDto) => {
        return <div>{row.translatedName}</div>
    }

    const renderColorCell = (row: GenericTypeDto) => {
        return <div style={{
            width: 22,
            height: 22,
            backgroundColor: row.color,
            borderRadius: 50
        }}></div>
    }

    const tableColumns: ListingTableColumn<GenericTypeDto>[] = [
        {
            width: '22px',
            field: 'color',
            searchField: 'gt.color',
            renderCell: renderColorCell
        },
        {
            field: 'name',
            name: t('generic_types.name'),
            onSearch: onTableFilter,
            searchField: 'translated_name',
            renderCell: renderNameCell
        },
        {
            name: '',
            width: '60px',
            cellAlignment: 'right',
            preventClick: true,
            renderCell: renderTableActionCell,
            cellStyle: { overflow: 'unset' },
        }
    ];

    const getData = async () => {
        try {
            Loading.show();

            saveOrdernationFilters();

            // Always use fresh translations from current language
            const requestCriteria = {
                ...criteria,
                translations: t('common.generic_types', { returnObjects: true })
            };

            const page = await GenericTypesService.getList(requestCriteria);
            setGenericTypesPage(page);

            Loading.hide();
        } catch (error) {
            Logger.error(LOGGER_LOG_TYPE.REQUEST, `Couldn't get vehicle segments list`, error);
            addToast(t('common.messages.error_load_info'), { appearance: 'error' });
            Loading.hide();
        }
    };

    const onCancelRemove = () => {
        setItemToRemove(null);
        setShowRemoveModal(false);
    };

    const onRemove = async () => {
        if (itemToRemove === null) {
            addToast(t('common.messages.record_delete_error'), { appearance: 'error' });
            return;
        }

        try {
            await GenericTypesService.remove(itemToRemove);
            setCriteria({ ...criteria, page: 1 });
            onCancelRemove();
            addToast(t('common.messages.record_delete_success'), {
                appearance: 'success',
            });
        }
        catch (error) {
            Logger.error(LOGGER_LOG_TYPE.REQUEST, `Couldn't delete genericType`, error);
            addToast(t('common.messages.record_delete_error'), { appearance: 'error' });
        }
    };

    useEffect(() => {
        if (isFirstLoad.current == true) {
            loadFilters()
        } else {
            getData()
        }
    }, [criteria]);

    // Re-fetch data when language changes to get correct translations
    useEffect(() => {
        if (!isFirstLoad.current) {
            getData();
        }
    }, [i18n.language]);

    const saveOrdernationFilters = () => {
        saveFiltersOnCache(criteria);
    }

    const saveFiltersOnCache = (f: GenericTypeSearchCriteria) => {
        // Don't cache translations - they should always be fresh from current language
        const { translations, ...filtersWithoutTranslations } = f as any;
        localStorage.setItem(filterJsonKey, JSON.stringify(filtersWithoutTranslations));
    }

    const loadFilters = () => {
        const cachedFilters = localStorage.getItem(filterJsonKey);
        if (cachedFilters != null) { onChangeFilters(JSON.parse(cachedFilters), true) }
        else { getData() }

        isFirstLoad.current = false;
    }

    const onChangeFilters = (f: GenericTypeSearchCriteria, isOnLoad?: boolean) => {
        saveFiltersOnCache(f);
        setCriteria(prev => ({
            ...prev,
            ...f,
            page: (isOnLoad && f.page) ? f.page : 1,
        }))
    }

    return (
        <div className='list'>
            <div className={styles.tabHeader}>
                <div>
                    <InputSearch defaultValue={criteria.allIn ?? undefined} onChangeValue={debounced} placeholder={t('generic_types.search_placeholder')} ></InputSearch>
                </div>
                <div className={styles.rowFlex}>
                    {hasGenericTypesWritePolicy && <ScreenHeaderButton icon={AddIcon} onClick={() => history.push(`/settings/genericTypes/new`)} />}
                </div>
            </div>
            {!isFirstLoad.current && <ListingTable
                columns={tableColumns}
                rows={genericTypesPage?.items || []}
                onHref={row => `/settings/genericTypes/details/${row.id ?? ''}`}
                allowHover={true}
                initialSearch={{ colField: criteria.orderColumn ?? 'translated_name', isOrderAsc: (criteria.orderBy && criteria.orderBy == 'asc') ? true : false }}
            />}
            <PaginationWithInfo
                itemName={t('generic_types.generic_types')}
                currentPage={genericTypesPage?.currentPage || 1}
                pageItems={genericTypesPage?.items.length || 0}
                totalItems={genericTypesPage?.totalItems || 0}
                onChange={page => setCriteria({ ...criteria, page })}
            />
            <QuestionYesNo onNo={onCancelRemove} onYes={onRemove} isVisible={showRemoveModal} message={t('common.messages.remove_generic_type', { genericType: itemToRemove?.name ?? '' })} />
        </div>
    );
};

export default GenericTypesList;
